import dayjs from "dayjs";
import debounce from "lodash.debounce";
import { useInterval } from "react-use";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts";
import { useAppDispatch, useAppSelector } from "@/stores";
import { DateHelper, FileHelper, FormHelper } from "@/helpers";
import { UserCategory, UserService } from "@/domains/user";
import {
  TransferCategory,
  TransferQueryParams,
  TransferDepositStatusSearchOptions,
  TransferService,
  TransferQueryFormData,
} from "@/domains/transfer";
import { tenantSelfWithdrawActions, tenantSelfWithdrawSelects } from "./store";
// components
import { Button, DatePicker, Form, Input, Select, notification } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { CreateModal } from "./CreateModal";

type TransferQueryParamsFormValues = QueryParamsFormValues<TransferQueryParams>;

export const TableTopFilters = () => {
  const auth = useAuth();

  const dispatch = useAppDispatch();

  const [form] = Form.useForm<TransferQueryFormData>();

  const queryParams = useAppSelector(tenantSelfWithdrawSelects.queryParams);

  const [belongs, setBelongs] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    const queryParamsFormValues = {
      ...DateHelper.convertStringToDayjs(queryParams),
      ...FormHelper.getQueryParamsDateTypeRange(queryParams),
    };

    form.setFieldsValue({
      ...queryParamsFormValues,
      belong: queryParamsFormValues.belong
        ? queryParamsFormValues.belong.split(",")
        : [],
      status: queryParamsFormValues.status
        ? queryParamsFormValues.status.split(",").map(Number)
        : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = debounce((values: TransferQueryParamsFormValues) => {
    const newQueryParams = DateHelper.convertDayjsToString(values);
    dispatch(tenantSelfWithdrawActions.setQueryParams(newQueryParams));
  }, 400);

  const convertToParamsForm = (values: TransferQueryFormData) => {
    const formValues = {
      ...values,
      [`${values.dateType}_started_at`]: values.dateRange[0],
      [`${values.dateType}_stopped_at`]: values.dateRange[1],
      belong: values.belong?.join(","),
      status: values.status?.join(","),
      dateType: undefined,
      dateRange: undefined,
    };

    return formValues satisfies TransferQueryParamsFormValues;
  };

  const handleValuesChange = (_: any, values: TransferQueryFormData) => {
    search(convertToParamsForm(values));
  };

  const handleDownload = async () => {
    const res = await TransferService.download({
      ...queryParams,
      category: TransferCategory.MemberDeposit,
    }).run();

    FileHelper.download({ data: res.data });
  };

  const loopGetMemberDepositTransfers = () => {
    if (import.meta.env.VITE_DISABLE_LONG_POLLING === "true") return;

    const formValue = form.getFieldsValue();

    const timeNow = dayjs();
    const timeStartedAt = dayjs(formValue.dateRange[0]);
    const timeStoppedAt = dayjs(formValue.dateRange[1]);
    if (
      timeNow.diff(timeStoppedAt, "day") > 1 &&
      timeStartedAt.isStartOf("day") &&
      timeStoppedAt.isEndOf("day")
    ) {
      return;
    }
    if (timeNow.date() !== timeStoppedAt.date()) {
      notification.error({
        message: "時間範圍",
        description: "已跨日請重整網頁",
      });
    }

    search(convertToParamsForm(formValue));
  };

  useInterval(loopGetMemberDepositTransfers, 10000);

  useEffect(() => {
    void (async () => {
      if (!auth.user) return;

      const isAccount = auth.user.category === UserCategory.TenantAccount;

      const parentId = isAccount ? auth.user.id : auth.user.parent_id!;

      const res = await UserService.list({
        parent_id: parentId,
        category: UserCategory.TenantAccount,
      }).run();

      setBelongs(res.data.msg.users.map((u) => ({ id: u.id, name: u.name })));
    })();
  }, [auth.user]);

  return (
    <Form
      form={form}
      labelCol={{ className: "w:80" }}
      onValuesChange={handleValuesChange}
    >
      <div className="flex:col gap:16">
        <div className="flex ai:center gap:16">
          <Form.Item label="平台目標" name="belong" className="flex:1">
            <Select
              allowClear
              mode="multiple"
              placeholder="全部"
              maxTagCount="responsive"
              options={belongs.map((belong) => ({
                label: belong.name,
                value: belong.id,
              }))}
            ></Select>
          </Form.Item>
          <Form.Item label="平台單號" name="order" className="flex:1">
            <Input allowClear placeholder="可模糊搜尋" />
          </Form.Item>
          <Form.Item label="平台會員" name="member" className="flex:1">
            <Input allowClear placeholder="可模糊搜尋" />
          </Form.Item>
          <div className="mb:16 flex:1 flex ai:center jc:end gap:16">
            <Button
              type="primary"
              htmlType="button"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload()}
            >
              下載報表
            </Button>

            <CreateModal />
          </div>
        </div>

        <div className="flex ai:center gap:16">
          <Form.Item label="系統單號" name="id" className="flex:1">
            <Input allowClear placeholder="可模糊搜尋" />
          </Form.Item>
          <Form.Item label="交易狀態" name="status" className="flex:1">
            <Select
              allowClear
              mode="multiple"
              placeholder="全部"
              maxTagCount="responsive"
              options={TransferDepositStatusSearchOptions.slice()}
            ></Select>
          </Form.Item>
          <Form.Item label="交易備註" name="note" className="flex:1">
            <Input allowClear placeholder="可模糊搜尋" />
          </Form.Item>
          <div className="flex:1" />
        </div>

        <div className="flex ai:center gap:16">
          <Form.Item label="打款地址" name="source" className="flex:1">
            <Input allowClear placeholder="可模糊搜尋" />
          </Form.Item>
          <Form.Item label="收款地址" name="target" className="flex:1">
            <Input allowClear placeholder="可模糊搜尋" />
          </Form.Item>
          <Form.Item label="時間類型" name="dateType" className="flex:1">
            <Select
              allowClear
              options={[
                { label: "建立時間", value: "created" },
                { label: "檢核時間", value: "checked" },
                { label: "到帳時間", value: "arrived" },
              ]}
            ></Select>
          </Form.Item>
          <Form.Item label="時間範圍" name="dateRange" className="flex:1">
            <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm" />
          </Form.Item>
        </div>
      </div>
    </Form>
  );
};
